/** Bounded session refresh — single in-flight refresh for concurrent 401 responses. */

import {
  isGenerationCurrent,
  markNetworkError,
  markRefreshing,
  shouldForceLogoutOnRefreshFailure,
} from "@/lib/authSessionCoordinator";

let refreshPromise = null;

export function resetRefreshQueueForTests() {
  refreshPromise = null;
}

export function refreshSession(api) {
  if (!refreshPromise) {
    markRefreshing();
    refreshPromise = api
      .post("/auth/refresh")
      .then((res) => res)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const AUTH_SKIP_REFRESH = ["/auth/login", "/auth/refresh", "/auth/register", "/auth/logout"];

function shouldAttemptRefresh(url = "") {
  return !AUTH_SKIP_REFRESH.some((segment) => url.includes(segment));
}

function isRequestCancelled(error) {
  return error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
}

export function installSessionRefreshInterceptor(api, { onSessionExpired } = {}) {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const status = error?.response?.status;
      if (!config || config.__sessionRefreshRetry || status !== 401) {
        return Promise.reject(error);
      }
      if (!shouldAttemptRefresh(config.url || "")) {
        return Promise.reject(error);
      }
      try {
        await refreshSession(api);
        config.__sessionRefreshRetry = true;
        return api.request(config);
      } catch (refreshError) {
        if (shouldForceLogoutOnRefreshFailure(refreshError)) {
          onSessionExpired?.();
        }
        return Promise.reject(refreshError);
      }
    }
  );
}

export async function hydrateAuthSession(api, { generationToken, signal } = {}) {
  if (generationToken != null && !isGenerationCurrent(generationToken)) {
    throw new Error("hydrate superseded");
  }

  try {
    const res = await api.get("/auth/me", { signal });
    return { user: res.data.user, authenticated: true };
  } catch (error) {
    if (isRequestCancelled(error)) {
      throw error;
    }
    if (generationToken != null && !isGenerationCurrent(generationToken)) {
      throw new Error("hydrate superseded");
    }
    if (error?.response?.status !== 401) {
      if (!error?.response) {
        markNetworkError();
      }
      throw error;
    }
    try {
      await refreshSession(api);
    } catch (refreshError) {
      if (isRequestCancelled(refreshError)) {
        throw refreshError;
      }
      throw refreshError;
    }
    if (generationToken != null && !isGenerationCurrent(generationToken)) {
      throw new Error("hydrate superseded");
    }
    const res = await api.get("/auth/me", { signal });
    return { user: res.data.user, authenticated: true };
  }
}
